var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { IconContainer, InputContainer, Label, LabelContainer, RequiredLabel, RightIconsContainer, StyledInput, StyledInputWrapper, StyledTextarea, SuggestionItem, SuggestionsContainer, SupportText, TooltipButton } from "./InputText.style";
import { applyMask, getMaxLength, getPlaceholder, getUnmaskedValue, } from "../../../utils/inputMasks";
import { InfoIcon } from "../../../assets/icons/InfoIcon";
import { CheckIcon } from "../../../assets/icons/CheckIcon";
import { CloseIcon } from "../../../assets/icons/CloseIcon";
import { Tooltip } from "../../molecules/Tooltip/Tooltip";
import { colors } from "../../../base/styled/variables.styled";
import EyeIcon from "../../../assets/icons/EyeIcon";
export const InputText = (_a) => {
    var { value: controlledValue, onChange, placeholder, mask = "none", maxLength, defaultValue = "", multiline = false, rows = 4, label, supportText, isError = false, isSuccess = false, tooltip, customIcon, className, id, suggestions: externalSuggestions = [], onSuggestionClick, fetchSuggestions, minCharsForSuggestions = 3, showSuggestions = false, allowClear = false, showRequirement = false } = _a, props = __rest(_a, ["value", "onChange", "placeholder", "mask", "maxLength", "defaultValue", "multiline", "rows", "label", "supportText", "isError", "isSuccess", "tooltip", "customIcon", "className", "id", "suggestions", "onSuggestionClick", "fetchSuggestions", "minCharsForSuggestions", "showSuggestions", "allowClear", "showRequirement"]);
    const [internalValue, setInternalValue] = useState(defaultValue);
    const [internalSuggestions, setInternalSuggestions] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const inputWrapperRef = useRef(null);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const isControlled = controlledValue !== undefined;
    const value = isControlled ? controlledValue : internalValue;
    const isPasswordField = !multiline && props.type === "password";
    const effectiveInputType = isPasswordField
        ? isPasswordVisible
            ? "text"
            : "password"
        : props.type || "text";
    const hasCustomIcon = !!customIcon;
    const hasTooltip = !!tooltip;
    const showClear = allowClear &&
        typeof value === "string" &&
        value.length > 0 &&
        !props.disabled;
    const hasRightIcons = hasCustomIcon || hasTooltip || isError || isSuccess || showClear || isPasswordField;
    // Use external suggestions if provided, otherwise use internal ones
    const displaySuggestions = externalSuggestions.length > 0 ? externalSuggestions : internalSuggestions;
    const shouldShowSuggestions = showSuggestions &&
        isFocused &&
        displaySuggestions.length > 0 &&
        typeof value === "string" &&
        value.length >= minCharsForSuggestions;
    const handleFetchSuggestions = useCallback((term) => __awaiter(void 0, void 0, void 0, function* () {
        if (term.length < minCharsForSuggestions || !fetchSuggestions) {
            setInternalSuggestions([]);
            return;
        }
        try {
            const results = yield fetchSuggestions(term);
            setInternalSuggestions(results);
        }
        catch (error) {
            console.error("Error fetching suggestions:", error);
            setInternalSuggestions([]);
        }
    }), [fetchSuggestions, minCharsForSuggestions]);
    useEffect(() => {
        if (typeof value === "string" && fetchSuggestions) {
            const timeoutId = setTimeout(() => {
                handleFetchSuggestions(value);
            }, 300);
            return () => clearTimeout(timeoutId);
        }
    }, [value, handleFetchSuggestions, fetchSuggestions]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (inputWrapperRef.current &&
                !inputWrapperRef.current.contains(event.target)) {
                setIsFocused(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);
    const handleChange = useCallback((event) => {
        const inputValue = event.target.value;
        if (mask !== "none" && !multiline) {
            const maskedValue = applyMask(inputValue, mask);
            const unmaskedValue = getUnmaskedValue(maskedValue, mask);
            const syntheticEvent = Object.assign(Object.assign({}, event), { target: Object.assign(Object.assign({}, event.target), { value: maskedValue }) });
            if (!isControlled) {
                setInternalValue(maskedValue);
            }
            onChange === null || onChange === void 0 ? void 0 : onChange(syntheticEvent, unmaskedValue);
        }
        else {
            if (!isControlled) {
                setInternalValue(inputValue);
            }
            onChange === null || onChange === void 0 ? void 0 : onChange(event);
        }
    }, [mask, isControlled, onChange, multiline]);
    const handleFocus = useCallback(() => {
        setIsFocused(true);
    }, []);
    const handleBlur = useCallback(() => {
        setTimeout(() => {
            setIsFocused(false);
        }, 150);
    }, []);
    const handleSuggestionSelect = useCallback((suggestion) => {
        const syntheticEvent = {
            target: {
                value: suggestion,
            },
        };
        if (!isControlled) {
            setInternalValue(suggestion);
        }
        setIsFocused(false);
        if (onSuggestionClick) {
            onSuggestionClick(suggestion);
        }
        if (onChange) {
            onChange(syntheticEvent);
        }
    }, [isControlled, onSuggestionClick, onChange]);
    const effectivePlaceholder = placeholder ||
        (mask !== "none" && !multiline ? getPlaceholder(mask) : "Digite aqui...");
    const effectiveMaxLength = maxLength ||
        (mask !== "none" && !multiline ? getMaxLength(mask) : undefined);
    return (_jsxs(InputContainer, { className: className, children: [label && (_jsx(LabelContainer, { children: _jsxs(Label, { htmlFor: id, disabled: props.disabled, children: [label, " ", showRequirement && (_jsx(RequiredLabel, { children: props.required ? "*" : "(Opcional)" }))] }) })), _jsxs(StyledInputWrapper, { ref: inputWrapperRef, style: { position: "relative" }, children: [multiline ? (_jsx(StyledTextarea, { id: id, value: value, onChange: handleChange, onFocus: handleFocus, onBlur: handleBlur, placeholder: effectivePlaceholder, maxLength: effectiveMaxLength, hasRightIcons: hasRightIcons, rows: rows, disabled: props.disabled, error: isError, success: isSuccess && !isError, autoComplete: "off" })) : (_jsx(StyledInput, Object.assign({ id: id, value: value, onChange: handleChange, onFocus: handleFocus, onBlur: handleBlur, placeholder: effectivePlaceholder, maxLength: effectiveMaxLength, hasRightIcons: hasRightIcons, disabled: props.disabled, error: isError, success: isSuccess && !isError }, props, { type: effectiveInputType, autoComplete: "off" }))), _jsxs(RightIconsContainer, { isMultiline: multiline, children: [showClear && (_jsx(TooltipButton, { type: "button", "aria-label": "Limpar", onClick: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const syntheticEvent = {
                                        target: { value: "" },
                                    };
                                    if (!isControlled) {
                                        setInternalValue("");
                                    }
                                    onChange === null || onChange === void 0 ? void 0 : onChange(syntheticEvent);
                                }, children: _jsx(CloseIcon, { width: "16", height: "16", iconColor: colors.brand500 }) })), (hasCustomIcon || hasTooltip) && (_jsx(TooltipButton, { type: "button", "aria-label": "Informa\u00E7\u00F5es adicionais", children: hasTooltip ? (_jsx(Tooltip, { headline: "Informa\u00E7\u00F5es", supportingText: tooltip, trigger: "hover", placement: "top", children: customIcon || _jsx(InfoIcon, {}) })) : (customIcon) })), isPasswordField && (_jsx(TooltipButton, { type: "button", "aria-label": isPasswordVisible ? "Ocultar senha" : "Mostrar senha", "aria-pressed": isPasswordVisible, onClick: () => setIsPasswordVisible((prev) => !prev), children: _jsx(EyeIcon, {}) })), isError && (_jsx(IconContainer, { children: _jsx(CloseIcon, { width: "24", height: "24", iconColor: colors.errorRedIcon }) })), isSuccess && !isError && (_jsx(IconContainer, { children: _jsx(CheckIcon, { width: "24", height: "24", iconColor: colors.successGreen }) }))] }), shouldShowSuggestions && !multiline && (_jsx(SuggestionsContainer, { children: _jsx("div", { className: "ps-search-bar-autocomplete-scroll", children: displaySuggestions.map((suggestion, index) => (_jsx(SuggestionItem, { onClick: () => handleSuggestionSelect(suggestion), children: suggestion }, index))) }) }))] }), supportText && (_jsx(SupportText, { isError: isError, isSuccess: isSuccess, disabled: props.disabled, children: supportText }))] }));
};
//# sourceMappingURL=InputText.js.map